import React, { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * Floating "Back to top" button.
 * Appears after the user scrolls 600px down. Fixed bottom-right, glass-morphism dark pill
 * that matches the developer-first docs aesthetic without competing with body content.
 *
 * Usage: drop <BackToTop /> once near the bottom of a page layout.
 */
const BackToTop = ({ offset = 600 }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > offset);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [offset]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Back to top"
      data-testid="back-to-top-btn"
      className={`fixed bottom-24 right-6 z-50 inline-flex flex-col items-center justify-center
        w-14 h-14 rounded-full bg-[#1a1a1a]/90 backdrop-blur-md text-white
        border border-white/10 shadow-xl shadow-black/25
        hover:bg-[#0f0f0f] hover:scale-105 hover:shadow-2xl
        transition-all duration-300 ease-out
        ${visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3 pointer-events-none'}`}
    >
      <ArrowUp size={16} strokeWidth={2} className="text-white mb-0.5" />
      <span className="text-[9px] font-semibold tracking-wide leading-none">TOP</span>
    </button>
  );
};

export default BackToTop;
